import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { X, Image as ImageIcon, Plus } from "lucide-react";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import DineInThunk from "../../../../services/thunks/DineInThunk";
import { friendlyDineInMessage } from "../../../../services/apiServices/DineInApiService";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import { ProductDetailsModal } from "../../../../interfaces/ProductObject";
import { resolveProductLine } from "../../../../services/helpers/DineInSummaryHelper";
import { productImageUrls } from "../../../../services/helpers/ProductImageHelper";
import { filterSpecAttributes } from "../../../features/Product/ProductDetailsV2/utils/SpecsFilter";
import useScrollLock from "../../../../hooks/useScrollLock";
import "./styles/DineInProductDetailsModal.css";

interface DineInProductDetailsModalProps {
  /** Undefined while fetchDineInProducts is still resolving this productId —
   *  same convention as DineInFeedItemCard. */
  product?: ProductDetailsModal;
  /** The row's chosen variant — re-sent by "Add one more". */
  variantId?: string;
  quantity: number;
  addedByName: string;
  addedAt: string;
  /** DineInSession.session.sessionId — needed to dispatch addDineInItems. */
  dineInId: string;
  onClose: () => void;
}

/**
 * Full-detail read-only view of one feed item, opened by tapping its card —
 * the card itself only has room for a single truncated line, so this
 * showcases everything already loaded for the product (description,
 * category, and whatever attributes filterSpecAttributes finds in
 * `details`) instead of duplicating that extraction logic here; it's the
 * same helper ProductDetailsV2's SpecsCard uses on the main product page.
 *
 * The one action is "Add one more" — reuses the existing addDineInItems
 * thunk exactly as DineInOrderFab does. There's deliberately no decrease/
 * remove control: the backend only supports adding items (confirmed via
 * dineInConvertor.ts — repeated adds sum onto the existing quantity), so a
 * "-" button would have nothing correct to call yet.
 */
const DineInProductDetailsModal: React.FC<DineInProductDetailsModalProps> = ({
  product,
  variantId,
  quantity,
  addedByName,
  addedAt,
  dineInId,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { triggerNotification } = useNotification();
  const [isAdding, setIsAdding] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useScrollLock(true, overlayRef);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const resolved = resolveProductLine(product, variantId);
  const title = resolved.displayTitle || "Menu item";
  const imageUrl = product ? productImageUrls(product)[0] : undefined;
  const attributes = product ? filterSpecAttributes(product.details) : [];
  const lineTotal = resolved.unitPrice != null ? resolved.unitPrice * quantity : null;

  const handleAddOne = async () => {
    if (!product?.productId || isAdding) return;
    setIsAdding(true);
    try {
      await dispatch(
        DineInThunk.addDineInItems({
          dineInId,
          items: [{ productId: product.productId, variantId, quantity: 1 }],
        })
      ).unwrap();
      triggerNotification("Added one more to your order.", NotificationType.SUCCESS);
    } catch (error) {
      const message =
        typeof error === "string"
          ? friendlyDineInMessage(error)
          : "Couldn't add another. Please try again.";
      triggerNotification(message, NotificationType.ERROR);
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div className="dine-in-product-modal-overlay" ref={overlayRef} onClick={onClose}>
      <div
        className="dine-in-product-modal modal-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dine-in-product-modal__media">
          {imageUrl ? (
            <img src={imageUrl} alt={title} className="dine-in-product-modal__image" />
          ) : (
            <div className="dine-in-product-modal__image-placeholder">
              <ImageIcon size={28} strokeWidth={1.5} />
            </div>
          )}
          <button
            type="button"
            className="dine-in-product-modal__close"
            onClick={onClose}
            aria-label="Close product details"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="dine-in-product-modal__body">
          {product?.category && (
            <span className="dine-in-product-modal__category">{product.category}</span>
          )}
          <h3 className="dine-in-product-modal__title">{title}</h3>
          {product?.description && (
            <p className="dine-in-product-modal__description">{product.description}</p>
          )}

          {attributes.length > 0 && (
            <div className="dine-in-product-modal__attributes">
              {attributes.map((attr) => (
                <div key={attr.label} className="dine-in-product-modal__attr-row">
                  <span className="dine-in-product-modal__attr-label">{attr.label}</span>
                  <span className="dine-in-product-modal__attr-value">
                    {attr.tags ? attr.tags.join(", ") : attr.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          <div className="dine-in-product-modal__footer">
            <div className="dine-in-product-modal__footer-info">
              {lineTotal != null && (
                <span className="dine-in-product-modal__price">
                  ₹{Math.round(lineTotal)}
                </span>
              )}
              <span className="dine-in-product-modal__meta">
                {quantity > 1 ? `×${quantity} · ` : ""}
                Added by {addedByName} &middot; {addedAt}
              </span>
            </div>
            <button
              type="button"
              className="dine-in-product-modal__add-btn"
              onClick={handleAddOne}
              disabled={!product || isAdding}
            >
              <Plus size={14} strokeWidth={2.5} />
              {isAdding ? "Adding..." : "Add one more"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DineInProductDetailsModal;
